import { useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import { Reveal, ASSETS } from "@/lib/motion";
import Nav from "@/components/bwg/Nav";
import Footer from "@/components/bwg/Footer";
import WhatsAppFab from "@/components/bwg/WhatsAppFab";
import InteriorHero from "@/components/bwg/InteriorHero";
import Seo from "@/components/bwg/Seo";
import { orgSchema, breadcrumbSchema } from "@/lib/schema";

const PRESS = [
  { outlet: "Moneycontrol", logo: ASSETS.moneycontrol, date: "21 Sep 2026",
    headline: "Tier-2 cities vs metros: Where should you invest in property?",
    href: "https://www.moneycontrol.com/news/business/personal-finance/tier-2-cities-vs-metros-where-should-you-invest-in-property-14034699.html" },
  { outlet: "Moneycontrol", logo: ASSETS.moneycontrol, date: "18 Sep 2026",
    headline: "Digital land records may change how India checks property titles before investing",
    href: "https://www.moneycontrol.com/news/business/real-estate/digital-land-records-may-change-how-india-checks-property-titles-before-investing-14031396.html" },
  { outlet: "Financial Express", logo: ASSETS.fe, date: "16 Sep 2026",
    headline: "How infrastructure is changing India's land markets",
    href: "https://www.financialexpress.com/opinion/how-infrastructure-is-changing-indias-land-markets/4340754/" },
  { outlet: "Tribune India", logo: ASSETS.tribune, date: "30 Jul 2026",
    headline: "The system behind every land fortune in India",
    href: "https://www.tribuneindia.com/news/advertorial-disclaimer/the-system-behind-every-land-fortune-in-india" },
  { outlet: "ANI News", logo: ASSETS.ani, date: "30 Jul 2026",
    headline: "The system behind every land fortune in India",
    href: "https://aninews.in/news/business/the-system-behind-every-land-fortune-in-india20260730134034/" },
  { outlet: "Silicon India", logo: ASSETS.silicon, date: "28 Jul 2026",
    headline: "The deal behind Bhaarat Wealth Group",
    href: "https://www.siliconindia.com/news/general/the-deal-behind-bhaarat-wealth-group-nid-241704-cid-1.html" },
  { outlet: "Deccan Herald", logo: ASSETS.deccan, date: "10 Mar 2026",
    headline: "Housing sales paused in Dubai as West Asia conflict continues",
    href: "https://m.dailyhunt.in/news/india/english/deccan+herald-epaper-deccan/housing+sales+paused+in+dubai+as+west+asia+conflict+continues-newsid-n704033705" },
  { outlet: "Times of India", logo: ASSETS.toi, date: "9 Feb 2026",
    headline: "Old buildings vs new projects: which are safer and more sustainable",
    href: "https://timesofindia.indiatimes.com/real-estate/news/old-buildings-vs-new-projects-which-are-safer-and-more-sustainable/articleshow/128109458.cms" },
  { outlet: "Times of India", logo: ASSETS.toi, date: "1 Feb 2026",
    headline: "Budget 2026 signals renewed momentum for real estate, focus on Tier-1 and Tier-2 cities",
    href: "https://timesofindia.indiatimes.com/real-estate/news/budget-2026-signals-renewed-momentum-for-real-estate-and-focus-on-tier-1-and-tier-2-cities/articleshow/127834938.cms" },
  { outlet: "Times of India", logo: ASSETS.toi, date: "26 Jan 2026",
    headline: "8 important things to know before investing in land",
    href: "https://timesofindia.indiatimes.com/real-estate/news/8-important-things-to-know-before-investing-in-land/articleshow/127526649.cms" },
  { outlet: "Times of India", logo: ASSETS.toi, date: "25 Jan 2026",
    headline: "What zoning really means for land value in Indian cities",
    href: "https://timesofindia.indiatimes.com/real-estate/news/what-zoning-really-means-for-land-value-in-indian-cities/articleshow/127411919.cms" },
];

export default function Press() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <main className="bg-brand-paper overflow-x-hidden">
      <Seo
        title="Featured On | Bhaarat Wealth Group in the Press"
        description="Bhaarat Wealth Group featured in the Times of India, Deccan Herald, Financial Express, Moneycontrol, Tribune India, ANI News and Silicon India. Read the coverage."
        path="/press"
        schema={[orgSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Featured On", path: "/press" }])]}
      />
      <Nav overHero />
      <InteriorHero img={ASSETS.registry} eyebrow="Featured On"
        title="Our perspective, in the press."
        sub="Where BWG has weighed in on land, zoning, FAR and the real drivers of value across India's markets." />

      <section className="bg-brand-offwhite py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-x-14 gap-y-8 mb-16 pb-12 border-b border-brand-ink/10" data-testid="press-logo-strip">
            <img src={ASSETS.toi} alt="The Times of India" className="h-8 lg:h-10 object-contain opacity-70" />
            <img src={ASSETS.deccan} alt="Deccan Herald" className="h-10 lg:h-12 object-contain opacity-70" />
            <img src={ASSETS.fe} alt="Financial Express" className="h-8 lg:h-10 object-contain opacity-70" />
            <img src={ASSETS.moneycontrol} alt="Moneycontrol" className="h-6 lg:h-8 object-contain opacity-70" />
            <img src={ASSETS.tribune} alt="Tribune India" className="h-6 lg:h-8 object-contain opacity-70" />
            <img src={ASSETS.ani} alt="ANI News" className="h-6 lg:h-8 object-contain opacity-70" />
            <img src={ASSETS.silicon} alt="Silicon India" className="h-6 lg:h-8 object-contain opacity-70" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRESS.map((p, i) => (
              <Reveal key={i} delay={(i % 3) * 0.1}>
                <a href={p.href} target="_blank" rel="noopener noreferrer nofollow" data-testid={`press-card-${i}`}
                  className="group flex flex-col justify-between h-full bg-brand-card border border-brand-ink/10 rounded-sm p-8 hover:border-brand-orange transition-colors duration-500 min-h-[220px]">
                  <div>
                    <div className="flex items-center justify-between gap-3 mb-6">
                      <img src={p.logo} alt={p.outlet} className="h-6 object-contain object-left opacity-70" />
                      <span className="text-xs font-mono text-brand-grey/70 shrink-0">{p.date}</span>
                    </div>
                    <h3 className="font-display font-semibold text-xl tracking-tight text-brand-ink leading-snug group-hover:text-brand-orange transition-colors">{p.headline}</h3>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-brand-grey group-hover:text-brand-orange transition-colors">
                    Visit the article <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
          <p className="mt-10 text-xs text-brand-grey">Outbound links open the publisher's site. BWG does not control external content.</p>
        </div>
      </section>

      <Footer />
      <WhatsAppFab />
    </main>
  );
}
